import React, { useEffect } from 'react';
import SEO from '../SEO';
import seoData from '../../seoData';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { CheckCircle, ArrowRight, Shield, Wheat, Fish, Wrench } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const AgricultureFishing = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title={seoData["industries/agriculture-fishing"]?.title} description={seoData["industries/agriculture-fishing"]?.description} path="/industries/agriculture-fishing" keywords={seoData["industries/agriculture-fishing"]?.keywords || []} />

      <PageHero
        badge="Industries We Serve"
        title="Agriculture & Fishing"
        description="Coating and repair for agricultural and fishing operations. Tank lining, equipment protection, floor coatings, and corrosion repair for farms, processing plants, and aquaculture facilities."
        backgroundKey="agriculture-fishing"
        buttonText="Get Industry Consultation"
      />

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Industry Challenges We Solve</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {['Fertilizer and chemical tank corrosion',
              'Processing floor wear and contamination risks',
              'Water storage and irrigation system deterioration',
              'Fish farm tank and pond lining failure',
              'Equipment corrosion from moisture and chemical exposure',
              'Concrete foundation and containment area degradation'].map((challenge, index) => (
              <div key={index} className="flex items-start p-4 bg-green-50 rounded-lg">
                <Wheat className="text-green-600 mr-3 mt-0.5 flex-shrink-0" size={20} />
                <span className="text-gray-700 text-sm">{challenge}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Our Solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card className="border-l-4 border-l-green-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Fish className="text-green-600 mr-3 flex-shrink-0" size={22} />
                  Tank Lining & Containment
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Chemical-resistant tank lining for fertilizer storage, pesticide tanks, and fuel storage. Secondary containment coatings for regulatory compliance. Water storage tank coating for irrigation systems.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-green-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Fish className="text-green-600 mr-3 flex-shrink-0" size={22} />
                  Processing Facility Coatings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Hygienic floor and wall coatings for agricultural processing plants, cold storage, and packing houses. FDA-compliant coatings for food contact surfaces.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-green-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Fish className="text-green-600 mr-3 flex-shrink-0" size={22} />
                  Aquaculture Tank Systems
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Fish-safe tank coatings and pond linings for aquaculture operations. Non-toxic coating systems for fish tanks, raceways, and hatchery facilities.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-green-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Fish className="text-green-600 mr-3 flex-shrink-0" size={22} />
                  Equipment & Structural Repair
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Pump, pipe, and equipment repair for irrigation systems, processing lines, and material handling equipment. Concrete repair for loading areas and foundations.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Choose BCS for Agriculture & Fishing?</h2>
              <div className="space-y-3">
                {['FDA and fish-safe coating options for food and aquaculture',
                  'Chemical resistance for fertilizers, pesticides, and agricultural chemicals',
                  'Extend tank and equipment service life',
                  'Reduce water loss from leaking tanks and irrigation systems',
                  'On-site repair minimizes equipment transport costs',
                  'Emergency repair services for critical harvest and processing seasons'].map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="text-green-700 mr-3 mt-0.5 flex-shrink-0" size={18} />
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-green-700 text-white p-8 rounded-lg">
              <Shield className="mb-4" size={48} />
              <h3 className="text-2xl font-bold mb-4">Agricultural Specialists</h3>
              <p className="text-green-100 mb-6">BCS provides coating solutions that work in the demanding agricultural environment — chemical resistance, FDA compliance, and durability for year-round operations.</p>
              <Button className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => navigate('/services/tank-repair')}>
                View Tank Services <ArrowRight className="ml-2" size={16} />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Protect Your Agricultural Assets</h2>
          <p className="text-blue-100 mb-8">Contact us for a free assessment of your agricultural tanks, equipment, and processing facilities.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => window.location.href = 'tel:+19493719948'}>
              Call (949) 371-9948
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-blue-800" onClick={() => navigate('/#contact')}>
              Request Consultation <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AgricultureFishing;
